import React, { useState, useEffect, useRef } from "react"
import { makeStyles } from "@material-ui/styles"
import { Grid, Box, Hidden } from "@material-ui/core"
import { NavLink } from "react-router-dom"
import { ActionCard } from "./ActionCard"
import { AuthorTools, Checkbox, MentionOutput, PriorityChip } from ".."
import { PRIORITY } from "../../data"
import { useMutationCompleteJobTask } from "../../data/jobs/useMutationCompleteJobTask"
import { useJobUtils, preventDefault } from "../../utils"
import { DueTimeActionCardInfo, HumanTimeActionCardInfo } from "./ProcessActionCard"
import { RowBox } from "../Boxes"
import { AssigneesActionCardInfo } from "."

const useStyles = makeStyles((theme) => ({
  info: {
    fontSize: 14,
    color: theme.palette.text.light,
    display: "flex",
    flexGrow: 1,
  },
  chip: {
    marginRight: theme.spacing(1),
  },
  stub: {
    marginLeft: -10,
  },
  complete: {
    color: theme.palette.success.main,
  },
}))

const TaskActionCard = ({ job, onEdit, date, navigable = true, onCantNavigate }) => {
  const {
    id,
    title,
    author,
    task: { description },
    availableFrom,
    pinning,
    status: { completed },
    priority,
  } = job
  const classes = useStyles()
  const { getNavigateToLink, isJobAfterToday, isJobOverdue } = useJobUtils()
  const [completeJobTask, { loading }] = useMutationCompleteJobTask(job)
  const initRef = useRef(false)
  const updateRef = useRef(false)
  const [complete, setComplete] = useState(false)

  const isScheduled = isJobAfterToday(availableFrom)
  const isOverdue = !complete && isJobOverdue(job)

  useEffect(() => {
    if (!initRef.current) {
      setComplete(completed)
      initRef.current = true
    }
  }, [completed])

  useEffect(() => {
    if (updateRef.current) {
      updateRef.current = false
    } else {
      setComplete(completed)
    }
  }, [completed])

  const handleCompletedChange = async (event) =>
    preventDefault(event, async () => {
      const state = !complete
      setComplete(state)
      await completeJobTask({
        variables: {
          id,
          complete: state,
        },
      })
    })

  const handleEdit = () => {
    if (onEdit) onEdit(job)
  }
  const handleDelete = () => {}

  const handleCantNavigateClick = () => {
    onCantNavigate && onCantNavigate()
  }

  return (
    <ActionCard
      title={title}
      detail={description ? <MentionOutput content={description} /> : null}
      completed={complete}
      overdue={!complete && isOverdue}
      isScheduled={isScheduled}
      cardProps={
        navigable
          ? { component: NavLink, to: { pathname: getNavigateToLink(job, date) } }
          : { onClick: handleCantNavigateClick }
      }
      stub={
        <Grid item className={classes.stub}>
          <Checkbox
            type="complete"
            checked={complete}
            onClick={handleCompletedChange}
            disabled={loading || isScheduled}
          />
        </Grid>
      }
      rightChildren={
        <Hidden xsUp>
          <AuthorTools item={job} author={author.id} onEdit={handleEdit} subject="task" onDelete={handleDelete} />
        </Hidden>
      }
      innerChildren={
        <Grid item>
          <Grid container direction="row" alignItems="center">
            {priority === PRIORITY.HIGH && <PriorityChip className={classes.chip} />}
          </Grid>
        </Grid>
      }
      footer={
        <>
          <Box alignItems="center" display="flex" flexDirection="row">
            <HumanTimeActionCardInfo date={availableFrom} pinning={pinning} />
            <DueTimeActionCardInfo job={job} forceComplete={complete} />
          </Box>
          <Box ml="auto">
            <RowBox>
              <AssigneesActionCardInfo job={job} />
            </RowBox>
          </Box>
        </>
      }
    />
  )
}

export { TaskActionCard }
